// EXTERNAL DEPENDENCIES
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

// HELPERS
import {
  useBusinessProfilePageContent,
  useBusinessProfilePageMessage,
  useBusinessProfileReduxState,
} from '@/screens/business-profile-page/hooks'
import { useProfileValidation } from '@/api/profile'
import { useMappedErrorContent } from '@/hooks'

// COMPONENTS
import { BodyText, Row, TextArea, Tooltip } from '@paypalcorp/pp-react'
import { WithLoadingShimmer } from '@/common/components/loading/with-loading-shimmer'

// REDUX
import {
  updateEditedProfileDataValues,
  updateAreValuesEdited,
} from '@/redux/features'

// CONSTANTS
import { BUSINESS_DESCRIPTION_SECTION_TEST_ID } from '../../constants/test-ids'

// STYLES
import styles from './styles.module.less'

/**
 * Displays and validates the given business description
 */
export function BusinessDescriptionSection({
  isLoading,
  setIsValid,
}: Readonly<{
  isLoading: boolean
  setIsValid: (newValidationStatus: boolean) => void
}>) {
  const dispatch = useDispatch()

  // STATE
  const [inputErrors, setInputErrors] = useState([])
  const {
    editedProfileData: { values },
  } = useBusinessProfileReduxState()

  const businessDescription =
    values.businessDescription === null ? '' : values.businessDescription

  // NETWORK REQUESTS
  const {
    data: businessDescriptionValidationResult,
    error: businessDescriptionValidationError,
    isPending: validationPending,
    mutateAsync: validateBusinessDescription,
  } = useProfileValidation()

  // INITIALIZE CONTENT
  const getContent = useBusinessProfilePageContent()
  const getMessage = useBusinessProfilePageMessage()

  const businessDescriptionLabelText = getContent('businessDescription')
  const invalidLengthErrorText = getContent('invalidLengthError')
  const businessDescriptionTooltipText = getMessage(
    'businessDescriptionTooltip',
    {
      firstSection: (msg: string) => <BodyText>{msg}</BodyText>,
      firstBulletPoint: (msg: string) => (
        <BodyText className={styles.bulletPoint}>{msg}</BodyText>
      ),
      secondBulletPoint: (msg: string) => (
        <BodyText className={styles.bulletPoint}>{msg}</BodyText>
      ),
      secondSection: (msg: string) => <BodyText>{msg}</BodyText>,
    },
  )

  const { mappedErrors, isValid } = useMappedErrorContent(
    businessDescriptionValidationResult,
    businessDescriptionValidationError,
    validationPending,
  )

  useEffect(() => {
    setInputErrors(mappedErrors)
    setIsValid(isValid)
  }, [mappedErrors, isValid, setIsValid])

  // CHANGE HANDLERS
  const updateBusinessDescription = (newBusinessDescription: string) => {
    setInputErrors([])

    dispatch(
      updateEditedProfileDataValues({
        businessDescription: newBusinessDescription,
      }),
    )
    dispatch(updateAreValuesEdited(true))
  }

  function handleValidation(e: React.ChangeEvent<HTMLInputElement>) {
    const businessDescriptionInput = e.target.value

    const newErrors: string[] = []

    // validate length
    const isInvalidLength = businessDescriptionInput.length > 250
    if (isInvalidLength) {
      setIsValid(false)
      newErrors.push(invalidLengthErrorText)
    } else if (!businessDescription.length) {
      // reset validity when no text
      setIsValid(true)
    }

    setInputErrors(newErrors)

    if (!newErrors.length && businessDescriptionInput.length) {
      validateBusinessDescription({
        request: { businessDescription },
      })
    }
  }

  return (
    <div
      className={styles.sectionContainer}
      data-testid={BUSINESS_DESCRIPTION_SECTION_TEST_ID}
    >
      <Row noGutters className={styles.businessDescriptionLabelContainer}>
        <BodyText strong>{businessDescriptionLabelText}</BodyText>
        <Tooltip
          name='businessDescriptionTooltip'
          description={businessDescriptionTooltipText}
          screenReaderText={businessDescriptionTooltipText}
        />
      </Row>
      <WithLoadingShimmer isLoading={isLoading}>
        <TextArea
          showCharacterCounter
          aria-invalid={!!inputErrors.length}
          value={businessDescription}
          name='businessDescriptionTextArea'
          label={getContent('businessDescriptionPlaceholder')}
          onChange={(e) =>
            updateBusinessDescription(
              e.target.value === '' ? null : e.target.value,
            )
          }
          onBlur={handleValidation}
          errorText={inputErrors.length ? inputErrors : null}
          helperText={getContent('businessDescriptionHelperText')}
          maxCharacters={250}
        />
      </WithLoadingShimmer>
    </div>
  )
}
